/**
 * Contas a Receber — "Completar operações antigas" (complemento em lote, aprovado em 24/10/2026). Por parceiro:
 * prévia das operações com os campos ausentes, o valor sugerido e a origem; seleção EXPLÍCITA (nada vem marcado) de
 * operações e campos; abatimento só com confirmação do percentual; prévia do efeito; aplicação com justificativa;
 * histórico dos lotes. Condições específicas e definições feitas na comissão são preservadas; parcelas em nota,
 * quitadas ou liberadas não mudam; repetir o lote não duplica nem sobrescreve. Propostas e Pedidos não são alterados.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';
import { navegar } from '../rotas.ts';
import { Ajuda, EstadoCarga, RolagemIndicada, useCarga } from '../comercial/ui.tsx';
import { Campo } from '../componentes/basicos.tsx';
import { dataBr, rs } from './ContasPagar.tsx';
import { cod } from './ContasReceber.tsx';

type Situacao = 'aplicavel' | 'igual' | 'especifica' | 'tratado' | 'definida_comissao' | 'sem_regra';
type Campo4 = 'liberacao' | 'repasse' | 'confirmacao' | 'abatimento';
interface CampoPrevia {
  campo: Campo4;
  rotulo: string;
  situacao: Situacao;
  situacaoRotulo: string;
  atual: string;
  sugerido: string | null;
  origemSugestao: string | null;
}
interface OperacaoPrevia {
  pedidoId: string;
  pedido: string;
  modalidade: string;
  cliente: string;
  documento: string | null;
  ocfs: string[];
  comissoes: string[];
  estimativa: string | null;
  campos: CampoPrevia[];
  aplicaveis: Campo4[];
  protegidos: string[];
  lotes: string[];
}
interface Previa {
  parceiro: { id: string; nome: string; cadastroAtualizadoEm: string | null; regra: unknown; regraTexto: { liberacao: string; repasse: string } | null };
  abatimentoVigente: { percentual: number; vigenteDesde: string } | null;
  operacoes: OperacaoPrevia[];
  resumo: { operacoes: number; comAplicaveis: number; especificas: number; excecoes: number; tratadas: number };
  aviso: string;
}
interface Efeito {
  previa?: boolean;
  lote: string;
  complementos: number;
  operacoes: number;
  efeitos: { comissao: string; condicao: string[]; estimativa: { antes: string | null; depois: string | null } | null; protegidos: number }[];
  ignorados: { pedido: string; campo: string; motivo: string }[];
}
const CAMPOS: { id: Campo4; rotulo: string }[] = [
  { id: 'liberacao', rotulo: 'Liberação (com reconhecimento do pagamento)' },
  { id: 'repasse', rotulo: 'Prazo de repasse' },
  { id: 'confirmacao', rotulo: 'Confirmação do valor' },
  { id: 'abatimento', rotulo: 'Abatimento da estimativa' },
];
const TOM: Record<Situacao, string> = { aplicavel: 'pend', igual: 'ni', especifica: 'ext', tratado: 'ok', definida_comissao: 'ext', sem_regra: 'ni' };
const selo = (texto: string, tom: string) => <span className={`selo-previa ${tom}`}>{texto}</span>;
const ROTULO_CURTO: Record<Situacao, string> = { aplicavel: 'Ausente', igual: 'Já definido', especifica: 'Específica', tratado: 'Completado', definida_comissao: 'Na comissão', sem_regra: 'Sem regra' };

export function TelaLegado() {
  const parceiros = useCarga(() => api.get<{ parceiroId: string; parceiro: string; operacoes: number; semCondicao: number; semAbatimento: number }[]>('/api/receber/legado/parceiros'), []);
  const lotes = useCarga(() => api.get<{ numero: string; parceiro: string; campos: string[]; abatimento: number | null; justificativa: string; em: string; por: string | null; operacoes: number; complementos: number }[]>('/api/receber/legado/lotes'), []);
  const [parceiro, setParceiro] = useState('');
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [ops, setOps] = useState<string[]>([]);
  const [campos, setCampos] = useState<Campo4[]>([]);
  const [abat, setAbat] = useState('');
  const [confirmaAbat, setConfirmaAbat] = useState(false);
  const [just, setJust] = useState('');
  const [efeito, setEfeito] = useState<Efeito | null>(null);
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aplicado, setAplicado] = useState<Efeito | null>(null);

  const carregar = async (id: string) => {
    setParceiro(id);
    setPrevia(null);
    setOps([]);
    setCampos([]);
    setEfeito(null);
    setErro('');
    setConfirmaAbat(false);
    if (!id) return;
    setCarregando(true);
    try {
      const p = await api.get<Previa>(`/api/receber/legado/${id}`);
      setPrevia(p);
      setAbat(p.abatimentoVigente ? String(p.abatimentoVigente.percentual).replace('.', ',') : '');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setCarregando(false);
    }
  };
  // qualquer mudança na seleção invalida a prévia do efeito
  useEffect(() => setEfeito(null), [ops, campos, abat, confirmaAbat]);
  const corpo = useMemo(() => ({ operacoes: ops, campos, ...(campos.includes('abatimento') ? { abatimento: abat.replace(',', '.'), confirmar_abatimento: confirmaAbat } : {}) }), [ops, campos, abat, confirmaAbat]);
  const enviar = async (previaSo: boolean) => {
    setErro('');
    setEnviando(true);
    try {
      const r = await api.post<Efeito>(`/api/receber/legado/${parceiro}`, { ...corpo, previa: previaSo, justificativa: just });
      if (previaSo) setEfeito(r);
      else {
        setAplicado(r);
        void lotes.recarregar();
        void parceiros.recarregar();
        await carregar(parceiro);
      }
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  };
  const marcar = <T,>(xs: T[], x: T, sim: boolean) => (sim ? [...xs, x] : xs.filter((y) => y !== x));
  const comAplicaveis = previa?.operacoes.filter((o) => o.aplicaveis.length) ?? [];
  const abatOk = !campos.includes('abatimento') || (confirmaAbat && abat.trim() !== '');

  return (
    <>
      <h1 className="rc-titulo-pagina">Completar operações antigas</h1>
      <p className="texto-secundario sem-margem">
        Preencher o parceiro hoje não altera as condições copiadas nas operações já enviadas. Aqui o Financeiro completa, por parceiro, só os campos <strong>ausentes</strong>, com prévia, seleção explícita e histórico. Propostas aceitas e condições originais não são alteradas.
      </p>
      <div className="rc-filtros rc-legado-parceiro">
        <Campo rotulo="Parceiro">
          {(id) => (
            <select id={id} value={parceiro} onChange={(e) => void carregar(e.target.value)} data-teste="legado-parceiro">
              <option value="">Escolha o parceiro…</option>
              {(parceiros.dados ?? []).map((g) => (
                <option key={g.parceiroId} value={g.parceiroId}>
                  {g.parceiro} · {g.operacoes} operação(ões){g.semCondicao ? ` · ${g.semCondicao} sem condição` : ''}{g.semAbatimento ? ` · ${g.semAbatimento} sem abatimento` : ''}
                </option>
              ))}
            </select>
          )}
        </Campo>
      </div>
      {!parceiros.dados && <EstadoCarga carregando={parceiros.carregando} erro={parceiros.erro} recarregar={parceiros.recarregar} teste="legado-parceiros" />}
      {aplicado && (
        <p className="aviso aviso-sucesso" data-teste="legado-aplicado">
          Lote {aplicado.lote} aplicado: {aplicado.complementos} complemento(s) em {aplicado.operacoes} operação(ões). O histórico fica abaixo e em cada comissão.
        </p>
      )}
      {erro && (
        <p className="aviso aviso-alerta" data-teste="legado-erro">
          {erro}
        </p>
      )}
      {carregando && <p className="texto-secundario">Carregando a prévia…</p>}
      {previa && (
        <>
          <section className="cartao" data-teste="legado-regra">
            <h2 className="rc-subtitulo">
              Regra atual de {previa.parceiro.nome}
              <Ajuda titulo="Origem da sugestão">É a regra do cadastro do parceiro hoje. Ela só entra nos campos ausentes das operações e campos que você selecionar; o lote registra a cópia dela.</Ajuda>
            </h2>
            {previa.parceiro.regraTexto ? (
              <p className="sem-margem">
                Liberação: <strong>{previa.parceiro.regraTexto.liberacao}</strong> · Repasse: <strong>{previa.parceiro.regraTexto.repasse}</strong>
                <span className="rc-sec">cadastro atualizado em {previa.parceiro.cadastroAtualizadoEm ? dataBr(previa.parceiro.cadastroAtualizadoEm.slice(0, 10)) : '—'}</span>
              </p>
            ) : (
              <p className="sem-margem">
                Parceiro sem regra de recebimento cadastrada: só o abatimento pode ser completado.{' '}
                <button type="button" className="botao-link" onClick={() => navegar(`/administracao/parceiros/${previa.parceiro.id}`)}>
                  Abrir o cadastro do parceiro
                </button>
              </p>
            )}
            <p className="rc-sec">
              {previa.resumo.operacoes} operação(ões) · {previa.resumo.comAplicaveis} com campos ausentes · {previa.resumo.especificas} com condição específica (preservada) · {previa.resumo.excecoes} com exceções · {previa.resumo.tratadas} já tratadas em lote
            </p>
          </section>

          <h3 className="rc-subtitulo">1. Campos a completar (nada vem marcado)</h3>
          <div className="rc-filtros" data-teste="legado-campos">
            {CAMPOS.map((c) => (
              <label key={c.id} className="caixa-marcar">
                <input type="checkbox" checked={campos.includes(c.id)} onChange={(e) => setCampos(marcar(campos, c.id, e.target.checked))} data-teste={`legado-campo-${c.id}`} /> {c.rotulo}
              </label>
            ))}
          </div>
          {campos.includes('abatimento') && (
            <div className="bloco-impacto" data-teste="legado-abatimento">
              <p className="sem-margem">
                Abatimento ausente no envio: nada é aplicado retroativamente sem a sua confirmação.{' '}
                {previa.abatimentoVigente ? `Sugestão: ${String(previa.abatimentoVigente.percentual).replace('.', ',')}% (parâmetro vigente desde ${dataBr(previa.abatimentoVigente.vigenteDesde)}).` : 'Sem parâmetro vigente: informe o percentual.'}
              </p>
              <div className="rc-filtros">
                <Campo rotulo="Percentual (%)">{(id) => <input id={id} inputMode="decimal" value={abat} onChange={(e) => setAbat(e.target.value.replace(/[^\d,.]/g, ''))} data-teste="legado-abatimento-pct" />}</Campo>
                <label className="caixa-marcar">
                  <input type="checkbox" checked={confirmaAbat} onChange={(e) => setConfirmaAbat(e.target.checked)} data-teste="legado-confirmar-abatimento" /> Confirmo aplicar {abat || '—'}% às operações selecionadas que não têm abatimento
                </label>
              </div>
            </div>
          )}

          <h3 className="rc-subtitulo">
            2. Operações ({ops.length} de {comAplicaveis.length} com campos ausentes)
            {comAplicaveis.length > 0 && (
              <button type="button" className="botao-link" onClick={() => setOps(ops.length === comAplicaveis.length ? [] : comAplicaveis.map((o) => o.pedidoId))} data-teste="legado-todas">
                {ops.length === comAplicaveis.length ? 'desmarcar todas' : 'marcar todas com campos ausentes'}
              </button>
            )}
          </h3>
          <RolagemIndicada className="rolagem quadro-tabela" teste="legado-operacoes">
            <table className="tabela tabela-compacta rc-fixo" data-teste="legado-tabela">
              <thead>
                <tr>
                  <th style={{ width: '5%' }} aria-label="Selecionar" />
                  <th style={{ width: '16%' }}>Operação</th>
                  {CAMPOS.map((c) => (
                    <th key={c.id} style={{ width: '17%' }}>
                      {c.id === 'liberacao' ? 'Liberação' : c.id === 'repasse' ? 'Repasse' : c.id === 'confirmacao' ? 'Confirmação' : 'Abatimento'}
                    </th>
                  ))}
                  <th style={{ width: '11%' }}>Exceções</th>
                </tr>
              </thead>
              <tbody>
                {previa.operacoes.map((o) => (
                  <tr key={o.pedidoId} data-teste={`legado-op-${o.pedido}`}>
                    <td>
                      <input type="checkbox" aria-label={`Selecionar ${o.pedido}`} disabled={!o.aplicaveis.length} checked={ops.includes(o.pedidoId)} onChange={(e) => setOps(marcar(ops, o.pedidoId, e.target.checked))} data-teste={`legado-sel-${o.pedido}`} />
                    </td>
                    <td>
                      {cod(o.pedido)}
                      <span className="rc-sec">{o.cliente}</span>
                      <span className="rc-sec">{cod([...o.ocfs, o.documento, ...o.comissoes].filter(Boolean).join(' · '))}</span>
                      <span className="rc-sec">estimativa {o.estimativa === null ? 'a confirmar' : rs(o.estimativa)}</span>
                    </td>
                    {CAMPOS.map((c) => {
                      const x = o.campos.find((y) => y.campo === c.id)!;
                      return (
                        <td key={c.id} data-teste={`legado-${o.pedido}-${c.id}`} title={x.situacaoRotulo}>
                          {selo(ROTULO_CURTO[x.situacao], TOM[x.situacao])}
                          <span className="rc-sec">{x.situacao === 'aplicavel' ? `sugerido: ${x.sugerido ?? '—'}` : x.atual}</span>
                        </td>
                      );
                    })}
                    <td>
                      {o.protegidos.length ? <span className="rc-sec">{o.protegidos.length} parcela(s) em nota, quitada(s) ou liberada(s): não mudam</span> : <span className="texto-secundario">—</span>}
                      {o.lotes.length > 0 && <span className="rc-sec">lote {o.lotes.join(', ')}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </RolagemIndicada>
          <p className="rc-info">{previa.aviso}</p>

          <h3 className="rc-subtitulo">3. Prévia e aplicação</h3>
          <Campo rotulo="Justificativa (obrigatória para aplicar)">{(id) => <input id={id} value={just} maxLength={500} onChange={(e) => setJust(e.target.value)} placeholder="Ex.: completar operações anteriores à parametrização do parceiro" data-teste="legado-justificativa" />}</Campo>
          <div className="acoes-direita">
            <button type="button" className="botao-secundario" disabled={!ops.length || !campos.length || !abatOk || enviando} onClick={() => void enviar(true)} data-teste="legado-ver-previa">
              Ver prévia do efeito
            </button>
            <button type="button" className="botao-primario" disabled={!efeito || just.trim().length < 10 || enviando} onClick={() => void enviar(false)} data-teste="legado-aplicar">
              Aplicar lote
            </button>
          </div>
          {efeito && (
            <section className="bloco-impacto" data-teste="legado-efeito">
              <p className="sem-margem">
                <strong>{efeito.complementos}</strong> complemento(s) em {efeito.operacoes} operação(ões). Nada foi gravado ainda.
              </p>
              {efeito.efeitos.length > 0 && (
                <ul className="rc-hist">
                  {efeito.efeitos.map((e) => (
                    <li key={e.comissao}>
                      <span>{e.comissao}</span>
                      <span>
                        {e.condicao.length ? `condição: ${e.condicao.join(', ')}` : 'condição sem mudança'}
                        {e.estimativa ? ` · estimativa ${e.estimativa.antes === null ? 'a confirmar' : rs(e.estimativa.antes)} → ${e.estimativa.depois === null ? 'a confirmar' : rs(e.estimativa.depois)}` : ''}
                        {e.protegidos ? ` · ${e.protegidos} parcela(s) protegida(s)` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {efeito.ignorados.length > 0 && (
                <p className="rc-sec">
                  Não aplicado (preservado): {efeito.ignorados.map((x) => `${x.pedido} · ${x.campo} (${x.motivo})`).join('; ')}
                </p>
              )}
            </section>
          )}
        </>
      )}

      <details className="rc-recolhido" data-teste="legado-lotes">
        <summary>Lotes aplicados ({lotes.dados?.length ?? 0})</summary>
        <div>
          {(lotes.dados ?? []).length === 0 ? (
            <p className="texto-secundario sem-margem">Nenhum lote aplicado.</p>
          ) : (
            <ul className="rc-hist">
              {(lotes.dados ?? []).map((l) => (
                <li key={l.numero}>
                  <span>{new Date(l.em).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</span>
                  <span>
                    {l.numero} · {l.parceiro} · {l.campos.length} campo(s){l.abatimento !== null ? ` · abatimento ${String(l.abatimento).replace('.', ',')}% confirmado` : ''} · {l.operacoes} operação(ões), {l.complementos} complemento(s) · autorizado por {l.por ?? '—'} · {l.justificativa}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </>
  );
}
